import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import { IoPricetagsOutline } from "react-icons/io5";
import { usePagedTags } from "api/TagResource/useTags";
import BannerLoadingIndicator from "./BannerLoadingIndicator";

/**
 * Horizontal tag pills for the /books hero — same scroll + load-more pattern
 * as the filter sheet, styled for the green banner.
 */
const BrowseByTag = memo(({ selected = [], onToggle }) => {
	const scrollerRef = useRef(null);
	const scrollRafRef = useRef(null);
	const prevLengthRef = useRef(0);
	const [canScrollLeft, setCanScrollLeft] = useState(false);
	const [canScrollRight, setCanScrollRight] = useState(false);

	const pagedTags = usePagedTags({
		fromPortal: "no",
		rowsPerPage: 40,
		sort: "usage",
	});

	const items = useMemo(() => {
		const known = new Set(pagedTags.records.map((tag) => tag.name));
		const selectedOnly = selected
			.filter((name) => name && !known.has(name))
			.map((name) => ({ id: `selected-${name}`, name }));
		return [...selectedOnly, ...pagedTags.records];
	}, [pagedTags.records, selected]);

	const hasMore = pagedTags.hasMorePages;
	// Prefer the hook's page>1 flag so hover-prefetch / background refetch
	// (shared query key with Advanced search) does not remount this strip.
	const isLoadingMore = pagedTags.isLoadingMore;
	const onLoadMore = pagedTags.loadMore;

	const isNearEnd = useCallback((el) => {
		if (!el) return false;
		const lead = Math.max(el.clientWidth * 0.35, 80);
		return el.scrollLeft + el.clientWidth >= el.scrollWidth - lead;
	}, []);

	const maybeLoadMore = useCallback(() => {
		if (!hasMore || isLoadingMore) return;
		const el = scrollerRef.current;
		if (!el) return;
		if (isNearEnd(el) || el.scrollWidth <= el.clientWidth + 4) {
			onLoadMore?.();
		}
	}, [hasMore, isLoadingMore, isNearEnd, onLoadMore]);

	const updateScrollState = useCallback(() => {
		if (scrollRafRef.current) return;
		scrollRafRef.current = requestAnimationFrame(() => {
			scrollRafRef.current = null;
			const el = scrollerRef.current;
			if (!el) return;
			setCanScrollLeft(el.scrollLeft > 4);
			setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
			maybeLoadMore();
		});
	}, [maybeLoadMore]);

	useEffect(() => {
		const el = scrollerRef.current;
		if (!el) {
			setCanScrollLeft(false);
			setCanScrollRight(false);
			return undefined;
		}

		updateScrollState();

		const canObserve = typeof ResizeObserver !== "undefined";
		const observer = canObserve ? new ResizeObserver(updateScrollState) : null;
		if (observer) {
			observer.observe(el);
		} else {
			window.addEventListener("resize", updateScrollState);
		}

		return () => {
			if (observer) {
				observer.disconnect();
			} else {
				window.removeEventListener("resize", updateScrollState);
			}
			if (scrollRafRef.current) {
				cancelAnimationFrame(scrollRafRef.current);
				scrollRafRef.current = null;
			}
		};
	}, [items.length, hasMore, isLoadingMore, updateScrollState]);

	useEffect(() => {
		const el = scrollerRef.current;
		const previous = prevLengthRef.current;
		const grew = items.length > previous;
		const reset = items.length === 0 || previous === 0 || items.length < previous;

		// Keep the first paint at the start of the row. Only nudge forward when
		// more pills were appended while the user had already scrolled near the end.
		if (el && reset) {
			el.scrollLeft = 0;
			updateScrollState();
		} else if (el && grew && el.scrollLeft > 4 && isNearEnd(el)) {
			const distance = Math.max(el.clientWidth * 0.75, 180);
			el.scrollBy({ left: distance, behavior: "smooth" });
			updateScrollState();
		}

		prevLengthRef.current = items.length;
	}, [items.length, isNearEnd, updateScrollState]);

	const scrollByPage = (direction) => {
		const el = scrollerRef.current;
		if (!el) return;
		const distance = Math.max(el.clientWidth * 0.75, 180);

		if (direction > 0 && hasMore && !isLoadingMore && !canScrollRight) {
			onLoadMore?.();
			return;
		}

		if (direction > 0 && hasMore && !isLoadingMore && isNearEnd(el)) {
			onLoadMore?.();
		}

		el.scrollBy({ left: direction * distance, behavior: "smooth" });
	};

	const showRight = canScrollRight || hasMore;
	// Stay unmounted once the first load finishes empty — ignore background isFetching.
	const isEmpty = items.length === 0 && !pagedTags.isLoading;

	if (isEmpty) return null;

	return (
		<div className="catalogue-tags">
			<div className="catalogue-tags-label">
				<IoPricetagsOutline size={13} aria-hidden="true" />
				Browse by tag
				{typeof pagedTags.totalRecords === "number" && pagedTags.totalRecords > 0 && (
					<span className="font-normal normal-case tracking-normal text-white text-opacity-60">
						· {pagedTags.totalRecords}
					</span>
				)}
			</div>
			<div className="relative w-full min-w-0 py-1">
				<div
					ref={scrollerRef}
					onScroll={updateScrollState}
					role="group"
					aria-label="Browse by tag"
					aria-busy={pagedTags.isLoading || isLoadingMore}
					className="books-filter-row w-full min-w-0 max-w-full overflow-x-auto overflow-y-hidden px-11"
				>
					{/* A content-width row centers short lists without clipping overflowing tags. */}
					<div className="catalogue-tag-items">
						{items.map((tag) => {
							const isActive = selected.includes(tag.name);
							return (
								<button
									key={tag.id}
									type="button"
									onClick={() => onToggle(tag.name)}
									aria-pressed={isActive}
									className={`flex-shrink-0 whitespace-nowrap rounded-full border px-3 py-1.5 text-xs sm:text-sm font-medium transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-akgreener ${
										isActive
											? "border-white bg-white text-akgreenest"
											: "border-white border-opacity-30 bg-white bg-opacity-10 text-white hover:bg-opacity-20"
									}`}
								>
									{tag.name}
								</button>
							);
						})}
						{pagedTags.isLoading && (
							<span role="status" className="inline-flex flex-shrink-0 items-center gap-2">
								<span className="sr-only">Loading tags…</span>
								<span className="books-banner-placeholder h-8 w-10 animate-pulse rounded-full border border-white border-opacity-20 bg-white bg-opacity-20 sm:h-9 sm:w-20" aria-hidden="true" />
								<span className="books-banner-placeholder h-8 w-14 animate-pulse rounded-full border border-white border-opacity-20 bg-white bg-opacity-20 sm:h-9 sm:w-28" aria-hidden="true" />
								<span className="books-banner-placeholder h-8 w-10 animate-pulse rounded-full border border-white border-opacity-20 bg-white bg-opacity-20 sm:h-9 sm:w-24" aria-hidden="true" />
							</span>
						)}
						{isLoadingMore && (
							<span role="status" className="inline-flex flex-shrink-0">
								<BannerLoadingIndicator>Loading more tags…</BannerLoadingIndicator>
							</span>
						)}
					</div>
				</div>
				{canScrollLeft && (
					<span className="absolute inset-y-0 -left-4 z-10 flex items-center">
						<button
							type="button"
							onClick={() => scrollByPage(-1)}
							aria-label="Show previous tags"
							className="flex h-10 w-10 items-center justify-center rounded-full border border-white border-opacity-30 bg-white text-akgreenest shadow-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-white"
						>
							<FiChevronLeft className="block" size={22} />
						</button>
					</span>
				)}
				{showRight && (items.length > 0 || isLoadingMore) && (
					<span className="absolute inset-y-0 -right-4 z-10 flex items-center">
						<button
							type="button"
							onClick={() => scrollByPage(1)}
							disabled={isLoadingMore && !canScrollRight}
							aria-label="Show more tags"
							className="flex h-10 w-10 items-center justify-center rounded-full border border-white border-opacity-30 bg-white text-akgreenest shadow-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-white disabled:opacity-50"
						>
							<FiChevronRight className="block" size={22} />
						</button>
					</span>
				)}
			</div>
		</div>
	);
});

BrowseByTag.displayName = "BrowseByTag";

export default BrowseByTag;
